import { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Display-only rating stars
export function RatingDisplay({ rating, totalRatings, showCount = true, size = "sm" }) {
  const starSize = size === "sm" ? "w-4 h-4" : size === "md" ? "w-5 h-5" : "w-6 h-6";
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${starSize} ${
            star <= Math.round(rating)
              ? "fill-yellow-400 text-yellow-400"
              : "text-gray-600"
          }`}
        />
      ))}
      {showCount && (
        <span className="text-sm text-[#94A3B8] ml-1">
          {rating > 0 ? `${rating.toFixed(1)}` : "No ratings"}
          {totalRatings > 0 && ` (${totalRatings})`}
        </span>
      )}
    </div>
  );
}

// Interactive rating component for giving ratings
export function RatingInput({ workerId, workerName, projectId, onRatingAdded }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/worker-ratings`, {
        worker_id: workerId,
        project_id: projectId || null,
        rating: rating,
        comment: comment.trim() || null
      });
      toast.success(`Rating submitted for ${workerName}`);
      setOpen(false);
      setRating(0);
      setComment("");
      if (onRatingAdded) onRatingAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit rating");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
        >
          <Star className="w-4 h-4 mr-1" />
          Rate
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-400" />
            Rate {workerName}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Star Rating */}
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="focus:outline-none transition-transform hover:scale-110"
              >
                <Star
                  className={`w-10 h-10 transition-colors ${
                    star <= (hoverRating || rating)
                      ? "fill-yellow-400 text-yellow-400"
                      : "text-gray-600 hover:text-yellow-400/50"
                  }`}
                />
              </button>
            ))}
          </div>
          
          <p className="text-center text-[#94A3B8]">
            {rating === 0 && "Select a rating"}
            {rating === 1 && "Poor"}
            {rating === 2 && "Below Average"}
            {rating === 3 && "Average"}
            {rating === 4 && "Good"}
            {rating === 5 && "Excellent"}
          </p>
          
          {/* Comment */}
          <div className="space-y-2">
            <label className="text-sm text-[#94A3B8]">Comment (optional)</label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment about this worker's performance..."
              className="bg-black/50 border-white/10 text-white min-h-[80px]"
            />
          </div>
          
          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="w-full bg-yellow-500 text-black hover:bg-yellow-400 font-medium"
          >
            {submitting ? "Submitting..." : "Submit Rating"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RatingDisplay;
